import { ShieldCheck, Send, Lock, Unlock, XCircle, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api, errorText } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export const APPROVAL_STYLE = {
  draft: { label: "Draft", cls: "bg-slate-100 text-slate-700" },
  submitted: { label: "Submitted for approval", cls: "bg-amber-100 text-amber-800" },
  approved: { label: "Approved & locked", cls: "bg-emerald-600 text-white" },
  rejected: { label: "Rejected", cls: "bg-red-100 text-red-700" },
};

export const ApprovalBar = ({ projectId, approval, canSubmit, onChange }) => {
  const { can } = useAuth();
  const status = approval?.status || "draft";
  const st = APPROVAL_STYLE[status];
  const act = async (action, promptText) => {
    const comment = window.prompt(promptText, "");
    if (comment === null) return;
    try {
      await api.post(`/projects/${projectId}/approval/${action}`, { comment });
      toast.success({ submit: "Budget submitted for approval", approve: "Budget approved and locked", reject: "Budget rejected — back to draft editing", reopen: "Budget reopened for editing" }[action]);
      onChange();
    } catch (e) { toast.error(errorText(e)); }
  };
  const last = approval?.history?.[approval.history.length - 1];
  return (
    <div className="flex flex-wrap items-center gap-2 bg-white border border-slate-200 rounded-lg px-4 py-2.5" data-testid="approval-bar">
      <ShieldCheck size={16} className={status === "approved" ? "text-emerald-600" : "text-slate-400"} />
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Approval</span>
      <Badge className={`${st.cls} border-0`} data-testid="approval-status-badge">{st.label}</Badge>
      {last && <span className="text-[11px] text-slate-500 inline-flex items-center gap-1" data-testid="approval-last-action"><History size={12} /> {last.action} by {last.by} · {new Date(last.at).toLocaleString("en-GB")}{last.comment ? ` · "${last.comment}"` : ""}</span>}
      <div className="flex-1" />
      {(status === "draft" || status === "rejected") && can("edit") && <Button size="sm" disabled={!canSubmit} onClick={() => act("submit", "Comment for the approver (optional):")} className="bg-slate-900 hover:bg-emerald-700" data-testid="approval-submit-button"><Send size={14} className="mr-1.5" /> Submit for approval</Button>}
      {status === "submitted" && can("approve") && (
        <>
          <Button size="sm" onClick={() => act("approve", "Approval comment (optional):")} className="bg-emerald-700 hover:bg-emerald-800" data-testid="approval-approve-button"><Lock size={14} className="mr-1.5" /> Approve & lock</Button>
          <Button size="sm" variant="outline" onClick={() => act("reject", "Reason for rejection:")} className="text-red-700 border-red-200 hover:bg-red-50" data-testid="approval-reject-button"><XCircle size={14} className="mr-1.5" /> Reject</Button>
          <Button size="sm" variant="ghost" onClick={() => act("reopen", "Reason for reopening (optional):")} data-testid="approval-reopen-button"><Unlock size={14} className="mr-1.5" /> Withdraw</Button>
        </>
      )}
      {status === "approved" && can("approve") && <Button size="sm" variant="outline" onClick={() => act("reopen", "Reason for reopening the approved budget:")} data-testid="approval-reopen-button"><Unlock size={14} className="mr-1.5" /> Reopen for editing</Button>}
    </div>
  );
};
